import type { ExperimentOfferType, ExperimentStatus } from '../types';

// Where a test meets buyers (v2 design §2). The path picks the setup steps;
// every path runs the same test engine and lands in the same results view.
export type TestPath = 'in_whop' | 'link' | 'partner';

export interface PathDefinition {
  id: TestPath;
  title: string;
  quote: string;
  points: string[];
  next: string;
}

export const PATHS: PathDefinition[] = [
  {
    id: 'in_whop',
    title: 'Inside your whop',
    quote: 'Turn my free members into paid members',
    points: [
      'Free members see an upgrade offer inside your whop',
      'They buy without leaving Whop',
    ],
    next: 'Next: pick what to test, then the free product your members are in.',
  },
  {
    id: 'link',
    title: 'A link you share',
    quote: 'The link in my bio, pinned in Discord, in my YouTube description',
    points: [
      'Swap the link you already post for a test link',
      'One link for each place you post, all feeding the same test',
      'After a winner, the same link sells the winner',
    ],
    next: 'Next: pick what to test, then name where the link goes.',
  },
  {
    id: 'partner',
    title: 'Partners & ads',
    quote: 'Affiliates, clippers and ads send me traffic',
    points: [
      'One link per affiliate, clipper or campaign',
      'Affiliate credit and UTMs carry through',
      'See which offer wins for each source',
    ],
    next: 'Next: pick what to test, then add a link per partner.',
  },
];

export function isTestPath(value: string | null | undefined): value is TestPath {
  return value === 'in_whop' || value === 'link' || value === 'partner';
}

export function pathTitle(path: TestPath): string {
  return PATHS.find((p) => p.id === path)?.title ?? 'A link you share';
}

// A spot is one place a link is posted. Each becomes a `link` placement.
export type SpotId = 'bio' | 'discord' | 'telegram' | 'youtube' | 'email' | 'other';

export interface SpotDefinition {
  id: SpotId;
  label: string;
  // Preferred readable share code; a suffix is added if it's taken.
  code: string;
  howTitle: string;
  steps: string[];
}

export const SPOTS: SpotDefinition[] = [
  {
    id: 'bio',
    label: 'Bio link',
    code: 'bio',
    howTitle: 'Put it in your bio',
    steps: [
      'Open your Instagram, TikTok or X profile → Edit profile',
      'Replace your current Whop link with this one',
      'Mention the offer in your next post so people tap it',
    ],
  },
  {
    id: 'discord',
    label: 'Discord pin',
    code: 'discord',
    howTitle: 'Pin it in your free Discord',
    steps: [
      'Edit your pinned “how to join” message',
      'Swap the old checkout link for this one',
      'Re-pin so new members see it first',
    ],
  },
  {
    id: 'telegram',
    label: 'Telegram',
    code: 'tg',
    howTitle: 'Pin it in your Telegram channel',
    steps: [
      'Edit the pinned message in your free channel',
      'Replace the join link with this one',
      'Keep the button text the same',
    ],
  },
  {
    id: 'youtube',
    label: 'YouTube',
    code: 'yt',
    howTitle: 'Use it in YouTube descriptions',
    steps: [
      'Update the first link in your video descriptions',
      'Pin a comment with this link',
      'Use the same link in your channel header',
    ],
  },
  {
    id: 'email',
    label: 'Email & DMs',
    code: 'email',
    howTitle: 'Send it in email and DMs',
    steps: [
      'Use it in your next newsletter',
      'Save it as a quick reply for DMs',
      'Use this link instead of your old checkout link',
    ],
  },
  {
    id: 'other',
    label: 'Somewhere else',
    code: 'link',
    howTitle: 'Anywhere you post a link',
    steps: [
      'Name this link so you can tell it apart in results',
      'Replace the link you use there today',
      'Use a separate link for each place you post',
    ],
  },
];

export function spotById(id: SpotId): SpotDefinition {
  return SPOTS.find((s) => s.id === id) ?? SPOTS[SPOTS.length - 1];
}

// "Bio link" → its instructions, for placements created from a spot.
export function spotForLabel(label: string): SpotDefinition | undefined {
  return SPOTS.find((s) => s.label.toLowerCase() === label.trim().toLowerCase());
}

const SHARE_CODE_FORMAT = /^[a-z0-9](?:[a-z0-9-]{0,28}[a-z0-9])?$/;

/** "Mike's clips!" → "mikes-clips"; '' when nothing usable is left. */
export function slugify(text: string): string {
  const slug = text
    .toLowerCase()
    .replace(/['’]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 24)
    .replace(/-+$/g, '');
  return SHARE_CODE_FORMAT.test(slug) ? slug : '';
}

/** "Bio link" → "Bio link", "Discord pin" → "Discord pin link". */
export function linkName(label: string): string {
  const trimmed = label.trim();
  return /\blink$/i.test(trimmed) ? trimmed : `${trimmed} link`;
}

/**
 * A spot's name as sellers read it. Pre-v2 tests got their one link as a
 * placement labelled "Main link" by the migration. "Test link", because the
 * seller's original link is the one they post today, not this one.
 */
export const MAIN_LINK = 'Test link';

export function spotName(spot: { kind: string | null; label: string }): string {
  if (spot.kind === 'in_whop') return 'Inside your whop';
  return spot.label.trim().toLowerCase() === 'main link' ? MAIN_LINK : spot.label;
}

/** Where a test runs, in a few words: "2 links", "Inside your whop", "Inside your whop + 1 link". */
export function placesLabel(spots: Array<{ kind: string | null }>): string {
  const links = spots.filter((s) => s.kind !== 'in_whop').length;
  const linkWord = `${links} ${links === 1 ? 'link' : 'links'}`;
  if (links === spots.length) return linkWord;
  return links === 0 ? 'Inside your whop' : `Inside your whop + ${linkWord}`;
}

/** Our own generic names read as ordinary words mid-sentence; a seller's stay as typed. */
export function linkInSentence(label: string): string {
  const name = linkName(label);
  return name === MAIN_LINK || name === 'Unlock link' ? name.toLowerCase() : name;
}

/** "Copy Bio link", "Copy test link", "Copy Mike's clips link". */
export function copyLinkLabel(label: string): string {
  return `Copy ${linkInSentence(label)}`;
}

/** What decides where a test's links and in-whop offer send buyers. */
export interface TestState {
  status: ExperimentStatus;
  archived: boolean;
  offerType?: ExperimentOfferType; // absent = membership
}

type Spots = Array<{ kind: TestPath }>;

const hasLinks = (spots: Spots) => spots.some((p) => p.kind === 'link' || p.kind === 'partner');
const hasInWhop = (spots: Spots) => spots.some((p) => p.kind === 'in_whop');

/**
 * Where a test's links send buyers right now, as the router and offer page
 * decide it (src/services/router/routerService.ts, unlockOfferService):
 * a winner keeps selling on a membership link even when archived; an
 * unlock's offer page sells the winner only while it isn't archived;
 * anything else that isn't live goes to the regular store (membership) or
 * shows the offer as unavailable (unlock). `linkCount` picks "this link" or
 * "these links".
 */
export function linksNow(state: TestState, linkCount = 2): string {
  const unlock = state.offerType === 'unlock';
  const winner = state.status === 'winner_declared';
  const one = linkCount === 1;
  const links = one ? 'this link' : 'these links';
  const Links = one ? 'This link' : 'These links';
  // Verb agreement: "this link sends" / "these links send".
  const v = (verb: string) => (one ? `${verb}s` : verb);
  if (state.status === 'running' && !state.archived) {
    return `Anyone who opens ${links} is split between your offers.`;
  }
  if (winner && (!unlock || !state.archived)) return `${Links} ${v('sell')} the winning offer.`;
  const closed = unlock
    ? `${v('show')} the offer as unavailable. Past buyers can still open what they bought.`
    : `${v('send')} buyers to your regular Whop store.`;
  if (state.archived) return `Archived: ${links} ${closed}`;
  if (state.status === 'stopped') {
    return unlock
      ? `Paused: ${links} ${v('show')} the offer as unavailable until you resume. Past buyers can still open what they bought.`
      : `Paused: ${links} ${v('send')} buyers to your regular Whop store until you resume.`;
  }
  return unlock
    ? `Not live yet: ${links} ${v('show')} the offer as unavailable and nothing is tracked.`
    : `Not live yet: ${links} ${v('send')} buyers to your regular Whop store and nothing is tracked.`;
}

/** What archiving does to buyers, for the confirm before archiving. */
export function archiveEffect(spots: Spots, state: TestState): string {
  const winner = state.status === 'winner_declared';
  const unlock = state.offerType === 'unlock';
  const parts: string[] = [];
  if (hasInWhop(spots)) parts.push('free members stop seeing the offer inside your whop');
  if (hasLinks(spots)) {
    parts.push(
      winner && !unlock
        ? 'its links keep selling the winning offer'
        : unlock
          ? 'its links show the offer as unavailable (past buyers can still open what they bought)'
          : 'its links send buyers to your regular Whop store'
    );
  }
  if (state.status === 'running') parts.push('nothing new is tracked');
  const effect =
    parts.length === 0
      ? ''
      : `${parts.length > 1 ? `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}` : parts[0]}.`;
  const sentence = effect ? `${effect.charAt(0).toUpperCase()}${effect.slice(1)} ` : '';
  const after =
    state.status === 'winner_declared'
      ? 'It stays under Ended with its results kept, and you can unarchive it later.'
      : `It moves to Ended on your list with its results kept. You can unarchive it later to ${
          state.status === 'draft' ? 'finish setting it up' : 'resume it'
        }.`;
  return `${sentence}${after}`;
}

/**
 * What unarchiving does. The test keeps its status (archiving a live test
 * paused it), so a winner stays under Ended and anything else returns to
 * Drafts & paused.
 */
export function unarchiveHint(state: Pick<TestState, 'status'>): string {
  if (state.status === 'winner_declared') {
    return 'Unarchive it to keep it under Ended, selling the winner everywhere it was placed.';
  }
  if (state.status === 'draft') {
    return 'Unarchive it to move it back to Drafts & paused and finish setting it up.';
  }
  return 'Unarchive it to move it back to Drafts & paused, ready to resume.';
}
